import { gunzipSync, gzipSync } from 'fflate';

import { sha256Bytes } from '@/lib/parse/sha256';

import type { TrackFile } from './types';

/**
 * Turning a dropped file into a `TrackFile` row and back.
 *
 * Kept out of `repositories.ts` because none of it is about the storage
 * engine: compression and hashing are the same whether the row lands in
 * Dexie or in SQLite, and a pure function is one a test can call without a
 * database.
 */

const MEDIA_TYPES: Readonly<Record<string, string>> = {
  '.kml': 'application/vnd.google-earth.kml+xml',
  '.kmz': 'application/vnd.google-earth.kmz',
  '.gpx': 'application/gpx+xml',
  '.csv': 'text/csv',
  '.igc': 'text/plain',
  '.geojson': 'application/geo+json',
  '.json': 'application/json',
};

/** The media type a download of this file should carry, from its extension. */
export function mediaTypeFor(filename: string): string {
  const dot = filename.lastIndexOf('.');
  const extension = dot === -1 ? '' : filename.slice(dot).toLowerCase();
  return MEDIA_TYPES[extension] ?? 'application/octet-stream';
}

/**
 * Compression is only worth keeping when it pays for itself. A KMZ is already
 * deflated and an IGC is small; gzipping either spends CPU on every download
 * to save nothing, so anything that does not shrink by at least a tenth is
 * stored as it came.
 */
const WORTHWHILE_RATIO = 0.9;

export interface NewTrackFile {
  trackId: string;
  filename: string;
  bytes: Uint8Array;
  /** Fixed ids and clocks, so a test is not a moving target. */
  id?: string;
  addedAt?: string;
}

export function encodeTrackFile(input: NewTrackFile): TrackFile {
  const { bytes } = input;
  const gzipped = gzipSync(bytes, { level: 6 });
  const compress = gzipped.byteLength < bytes.byteLength * WORTHWHILE_RATIO;
  return {
    id: input.id ?? crypto.randomUUID(),
    trackId: input.trackId,
    filename: input.filename,
    mediaType: mediaTypeFor(input.filename),
    sizeBytes: bytes.byteLength,
    sha256: sha256Bytes(bytes),
    encoding: compress ? 'gzip' : 'identity',
    // A copy when stored as-is: the caller's view may sit on a larger buffer,
    // and IndexedDB would clone all of it.
    data: compress ? gzipped : bytes.slice(),
    addedAt: input.addedAt ?? new Date().toISOString(),
  };
}

/** The original bytes, exactly as they were dropped. */
export function decodeTrackFile(file: Pick<TrackFile, 'encoding' | 'data'>): Uint8Array {
  return file.encoding === 'gzip' ? gunzipSync(file.data) : file.data;
}
